import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Key,
  Plus,
  RefreshCw,
  Shield,
  Trash2,
  Edit,
  AlertCircle,
  CheckCircle,
  X,
  ArrowLeft,
  Copy,
  Eye,
  EyeOff,
  Ban,
  Calendar,
  Building2,
  Clock,
} from 'lucide-react';
import Header from '../../components/Header';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ConfirmDialog, { useConfirmDialog } from '../../components/ui/ConfirmDialog';
import {
  apiClient,
  ApiKey,
  CreateApiKeyRequest,
  CreateApiKeyResponse,
  UpdateApiKeyRequest,
  Tenant,
} from '../../services/api';

type ApiKeyStatus = 'active' | 'expired' | 'revoked' | 'inactive';

export default function ApiKeyManagement() {
  const navigate = useNavigate();
  const { accessToken, user: currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [filterTenantId, setFilterTenantId] = useState<string>('');

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState<ApiKey | null>(null);
  const [newRawKey, setNewRawKey] = useState<string>('');
  const [showRawKey, setShowRawKey] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);

  // Form states - Create
  const [createName, setCreateName] = useState('');
  const [createTenantId, setCreateTenantId] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createExpiresAt, setCreateExpiresAt] = useState('');

  // Form states - Edit
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);
  const [editExpiresAt, setEditExpiresAt] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { confirm: showConfirm, dialogProps } = useConfirmDialog();

  // Check if user is SUPER_ADMIN
  const isSuperAdmin = currentUser?.role === 'super_admin';

  useEffect(() => {
    if (isSuperAdmin) {
      loadApiKeys();
      loadTenants();
    }
  }, [accessToken, isSuperAdmin, filterTenantId]);

  const loadApiKeys = async () => {
    if (!accessToken) return;

    try {
      setLoading(true);
      const response = await apiClient.getApiKeys(accessToken, filterTenantId || undefined);
      if (response.success) {
        setApiKeys(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar API Keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTenants = async () => {
    if (!accessToken) return;

    try {
      const response = await apiClient.getAllTenants(accessToken);
      if (response.success && response.data?.data) {
        setTenants(response.data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar tenants:', error);
    }
  };

  const handleCreateApiKey = async () => {
    if (!accessToken) return;

    setError('');
    setSuccess('');

    if (!createName.trim()) {
      setError('Nome e obrigatorio');
      return;
    }

    try {
      setSaving(true);

      const data: CreateApiKeyRequest = {
        name: createName.trim(),
        tenantId: createTenantId || undefined, // Optional - leave empty for global key
        description: createDescription.trim() || undefined,
        expiresAt: createExpiresAt || undefined,
      };

      const response = await apiClient.createApiKey(data, accessToken);

      if (response.success) {
        // Store the raw key to show to user
        setNewRawKey(response.data.rawKey);
        setShowCreateModal(false);
        setShowKeyModal(true);

        // Reset form
        setCreateName('');
        setCreateTenantId('');
        setCreateDescription('');
        setCreateExpiresAt('');

        await loadApiKeys();
      }
    } catch (error: any) {
      setError(error.message || 'Erro ao criar API Key');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateApiKey = async () => {
    if (!accessToken || !selectedApiKey) return;

    setError('');
    setSuccess('');

    try {
      setSaving(true);

      const data: UpdateApiKeyRequest = {
        name: editName.trim() || undefined,
        description: editDescription.trim() || undefined,
        isActive: editIsActive,
        expiresAt: editExpiresAt || undefined,
      };

      const response = await apiClient.updateApiKey(selectedApiKey.id, data, accessToken);

      if (response.success) {
        setSuccess('API Key atualizada com sucesso!');
        setShowEditModal(false);
        setSelectedApiKey(null);
        await loadApiKeys();
      }
    } catch (error: any) {
      setError(error.message || 'Erro ao atualizar API Key');
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeApiKey = (apiKey: ApiKey) => {
    if (!accessToken) return;

    showConfirm({
      title: 'Revogar API Key',
      message: `Tem certeza que deseja revogar a API Key "${apiKey.name}"? Esta acao nao pode ser desfeita.`,
      variant: 'warning',
      confirmText: 'Revogar',
      onConfirm: async () => {
        try {
          await apiClient.revokeApiKey(apiKey.id, accessToken);
          setSuccess('API Key revogada com sucesso!');
          await loadApiKeys();
        } catch (error: any) {
          setError(error.message || 'Erro ao revogar API Key');
        }
      },
    });
  };

  const handleDeleteApiKey = (apiKey: ApiKey) => {
    if (!accessToken) return;

    showConfirm({
      title: 'Deletar API Key permanentemente',
      message: `Tem certeza que deseja DELETAR PERMANENTEMENTE a API Key "${apiKey.name}"?`,
      variant: 'danger',
      confirmText: 'Deletar',
      onConfirm: async () => {
        try {
          await apiClient.deleteApiKey(apiKey.id, accessToken);
          setSuccess('API Key deletada com sucesso!');
          await loadApiKeys();
        } catch (error: any) {
          setError(error.message || 'Erro ao deletar API Key');
        }
      },
    });
  };

  const openEditModal = (apiKey: ApiKey) => {
    setSelectedApiKey(apiKey);
    setEditName(apiKey.name);
    setEditDescription(apiKey.description || '');
    setEditIsActive(apiKey.isActive);
    setEditExpiresAt(apiKey.expiresAt ? apiKey.expiresAt.split('T')[0] : '');
    setShowEditModal(true);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    } catch (error) {
      console.error('Erro ao copiar:', error);
    }
  };

  const getStatusBadge = (status: ApiKeyStatus) => {
    switch (status) {
      case 'active':
        return { color: 'bg-success/20 text-success', label: 'Ativa' };
      case 'expired':
        return { color: 'bg-warning/20 text-warning', label: 'Expirada' };
      case 'revoked':
        return { color: 'bg-error/20 text-error', label: 'Revogada' };
      case 'inactive':
        return { color: 'bg-bg-3 text-text-3', label: 'Inativa' };
      default:
        return { color: 'bg-bg-3 text-text-3', label: status };
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Check permissions
  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen bg-bg-1">
        <Header title="Gerenciamento de API Keys" />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-bg-2 border border-error/30 rounded-lg p-8 text-center">
            <Shield size={48} className="mx-auto text-error mb-4" />
            <h2 className="text-xl font-semibold text-text-1 mb-2">Acesso Negado</h2>
            <p className="text-text-2">
              Apenas SUPER_ADMIN pode gerenciar API Keys.
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-1">
      <Header title="Gerenciamento de API Keys" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Messages */}
        {error && (
          <div className="mb-4 p-3 bg-error/10 border border-error/30 rounded-lg flex items-center gap-2 text-error">
            <AlertCircle size={18} />
            {error}
            <button onClick={() => setError('')} className="ml-auto">
              <X size={16} />
            </button>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-success/10 border border-success/30 rounded-lg flex items-center gap-2 text-success">
            <CheckCircle size={18} />
            {success}
            <button onClick={() => setSuccess('')} className="ml-auto">
              <X size={16} />
            </button>
          </div>
        )}

        {/* Back button */}
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center text-text-2 hover:text-text-1 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar ao Dashboard
        </button>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Key size={24} className="text-accent-1" />
            <div>
              <h1 className="text-2xl font-bold text-text-1">Gerenciamento de API Keys</h1>
              <p className="text-text-3 text-sm">Gerencie chaves de API para integracoes externas</p>
            </div>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="bg-bg-2 border border-bg-3 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div>
                <label className="block text-sm font-medium text-text-2 mb-1">Filtrar por Tenant</label>
                <select
                  value={filterTenantId}
                  onChange={(e) => setFilterTenantId(e.target.value)}
                  className="px-4 py-2 bg-bg-1 border border-bg-3 rounded-lg text-text-1 focus:outline-none focus:ring-2 focus:ring-accent-1 min-w-[200px]"
                >
                  <option value="">Todos os Tenants</option>
                  {tenants.map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={loadApiKeys}
                className="flex items-center gap-2 px-3 py-2 text-text-2 hover:text-text-1 transition-colors mt-6"
                title="Atualizar lista"
              >
                <RefreshCw size={18} />
              </button>
            </div>

            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-accent-1 hover:bg-accent-2 text-white rounded-lg transition-colors"
            >
              <Plus size={18} />
              Nova API Key
            </button>
          </div>
        </div>

        {/* API Keys List */}
        <div className="bg-bg-2 border border-bg-3 rounded-lg overflow-hidden">
          {loading ? (
            <LoadingSpinner size="lg" />
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-12">
              <Key size={48} className="mx-auto text-text-3 mb-3" />
              <p className="text-text-2">Nenhuma API Key encontrada</p>
              <p className="text-text-3 text-sm">Clique em "Nova API Key" para criar</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-bg-3">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-2 uppercase">
                    Nome / Prefixo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-2 uppercase">
                    Tenant
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-2 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-2 uppercase">
                    Expiracao
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-2 uppercase">
                    Ultimo Uso
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-2 uppercase">
                    Acoes
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-bg-3">
                {apiKeys.map((apiKey) => {
                  const statusBadge = getStatusBadge(apiKey.status);
                  const canRevoke = apiKey.status === 'active' || apiKey.status === 'inactive';

                  return (
                    <tr
                      key={apiKey.id}
                      className={`hover:bg-bg-3/50 ${apiKey.status !== 'active' ? 'opacity-60' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-text-1">{apiKey.name}</div>
                        <div className="text-xs text-text-3 font-mono">{apiKey.keyPrefix}...</div>
                        {apiKey.description && (
                          <div className="text-xs text-text-3 mt-1">{apiKey.description}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-sm text-text-1">
                          {apiKey.isGlobal ? (
                            <>
                              <span className="text-lg">🌐</span>
                              <span className="font-medium text-accent-1">Global</span>
                            </>
                          ) : (
                            <>
                              <Building2 size={14} className="text-text-3" />
                              {apiKey.tenantName || 'N/A'}
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusBadge.color}`}
                        >
                          {statusBadge.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-sm text-text-2">
                          <Calendar size={14} className="text-text-3" />
                          {apiKey.expiresAt ? formatDate(apiKey.expiresAt) : 'Nunca'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-text-2">
                          {apiKey.lastUsedAt ? (
                            <div className="flex items-center gap-1">
                              <Clock size={14} className="text-text-3" />
                              {formatDate(apiKey.lastUsedAt)}
                            </div>
                          ) : (
                            <span className="text-text-3">Nunca</span>
                          )}
                        </div>
                        {apiKey.lastUsedIp && (
                          <div className="text-xs text-text-3">IP: {apiKey.lastUsedIp}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEditModal(apiKey)}
                            className="p-1.5 text-text-3 hover:text-accent-1 transition-colors"
                            title="Editar"
                          >
                            <Edit size={16} />
                          </button>
                          {canRevoke && (
                            <button
                              onClick={() => handleRevokeApiKey(apiKey)}
                              className="p-1.5 text-text-3 hover:text-warning transition-colors"
                              title="Revogar"
                            >
                              <Ban size={16} />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteApiKey(apiKey)}
                            className="p-1.5 text-text-3 hover:text-error transition-colors"
                            title="Deletar"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Create API Key Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-bg-1 rounded-lg w-full max-w-md shadow-xl">
              <div className="flex items-center justify-between p-4 border-b border-border-1">
                <h3 className="text-lg font-semibold text-text-1">Nova API Key</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-text-3 hover:text-text-2"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-2 mb-1">
                    Nome <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    placeholder="Ex: Integracao CRM"
                    className="w-full px-4 py-2 bg-bg-1 border border-border-1 rounded-lg text-text-1 focus:outline-none focus:ring-2 focus:ring-accent-1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-2 mb-1">
                    Tenant (opcional)
                  </label>
                  <select
                    value={createTenantId}
                    onChange={(e) => setCreateTenantId(e.target.value)}
                    className="w-full px-4 py-2 bg-bg-1 border border-border-1 rounded-lg text-text-1 focus:outline-none focus:ring-2 focus:ring-accent-1"
                  >
                    <option value="">🌐 Global (acessa todos os tenants)</option>
                    {tenants.map((tenant) => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-text-3 mt-1">
                    Deixe vazio para criar uma chave global que pode acessar todos os tenants
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-2 mb-1">
                    Descricao (opcional)
                  </label>
                  <textarea
                    value={createDescription}
                    onChange={(e) => setCreateDescription(e.target.value)}
                    rows={2}
                    placeholder="Descreva o proposito desta API Key..."
                    className="w-full px-4 py-2 bg-bg-1 border border-border-1 rounded-lg text-text-1 focus:outline-none focus:ring-2 focus:ring-accent-1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-2 mb-1">
                    Data de Expiracao (opcional)
                  </label>
                  <input
                    type="date"
                    value={createExpiresAt}
                    onChange={(e) => setCreateExpiresAt(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2 bg-bg-1 border border-border-1 rounded-lg text-text-1 focus:outline-none focus:ring-2 focus:ring-accent-1"
                  />
                  <p className="text-xs text-text-3 mt-1">
                    Deixe em branco para nunca expirar
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 p-4 border-t border-border-1">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-bg-3 text-text-2 hover:bg-bg-3/80 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCreateApiKey}
                  disabled={saving || !createName}
                  className="flex items-center gap-2 px-4 py-2 bg-accent-1 hover:bg-accent-1/90 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {saving && <RefreshCw size={16} className="animate-spin" />}
                  Criar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Show Raw Key Modal */}
        {showKeyModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-bg-1 rounded-lg w-full max-w-lg shadow-xl">
              <div className="flex items-center justify-between p-4 border-b border-border-1">
                <h3 className="text-lg font-semibold text-text-1">API Key Criada</h3>
              </div>

              <div className="p-4 space-y-4">
                <div className="bg-warning/10 border border-warning/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="text-warning flex-shrink-0 mt-0.5" size={20} />
                    <div>
                      <p className="text-warning font-medium">Atencao!</p>
                      <p className="text-warning/80 text-sm">
                        Esta e a unica vez que voce vera esta chave. Copie e guarde em um local seguro.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-2 mb-2">
                    Sua API Key
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                      <input
                        type={showRawKey ? 'text' : 'password'}
                        value={newRawKey}
                        readOnly
                        className="w-full px-4 py-2 pr-20 bg-bg-3 border border-border-1 rounded-lg text-text-1 font-mono text-sm"
                      />
                      <button
                        onClick={() => setShowRawKey(!showRawKey)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-text-3 hover:text-text-2"
                      >
                        {showRawKey ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    <button
                      onClick={() => copyToClipboard(newRawKey)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                        keyCopied
                          ? 'bg-success/10 text-success'
                          : 'bg-bg-3 text-text-2 hover:bg-bg-3/80'
                      }`}
                    >
                      {keyCopied ? (
                        <>
                          <CheckCircle size={18} />
                          Copiado!
                        </>
                      ) : (
                        <>
                          <Copy size={18} />
                          Copiar
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <div className="bg-info/10 border border-info/30 rounded-lg p-4">
                  <p className="text-info text-sm">
                    <strong>Como usar:</strong> Adicione o header <code className="bg-info/20 px-1 rounded">X-API-Key</code> com esta chave nas suas requisicoes.
                  </p>
                </div>
              </div>

              <div className="flex justify-end p-4 border-t border-border-1">
                <button
                  onClick={() => {
                    setShowKeyModal(false);
                    setNewRawKey('');
                    setShowRawKey(false);
                  }}
                  className="px-4 py-2 bg-accent-1 hover:bg-accent-1/90 text-white rounded-lg transition-colors"
                >
                  Entendi, fechar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit API Key Modal */}
        {showEditModal && selectedApiKey && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-bg-1 rounded-lg w-full max-w-md shadow-xl">
              <div className="flex items-center justify-between p-4 border-b border-border-1">
                <h3 className="text-lg font-semibold text-text-1">Editar API Key</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-text-3 hover:text-text-2"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-4 space-y-4">
                <div className="bg-bg-3 border border-border-1 rounded-lg p-3">
                  <p className="text-sm text-text-3">Prefixo da Chave</p>
                  <p className="text-text-1 font-mono">{selectedApiKey.keyPrefix}...</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-2 mb-1">Nome</label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full px-4 py-2 bg-bg-1 border border-border-1 rounded-lg text-text-1 focus:outline-none focus:ring-2 focus:ring-accent-1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-2 mb-1">
                    Descricao
                  </label>
                  <textarea
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    rows={2}
                    className="w-full px-4 py-2 bg-bg-1 border border-border-1 rounded-lg text-text-1 focus:outline-none focus:ring-2 focus:ring-accent-1"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-text-2">
                    <input
                      type="checkbox"
                      checked={editIsActive}
                      onChange={(e) => setEditIsActive(e.target.checked)}
                      className="rounded border-border-1 bg-bg-1 text-accent-1 focus:ring-accent-1"
                    />
                    API Key Ativa
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-2 mb-1">
                    Data de Expiracao
                  </label>
                  <input
                    type="date"
                    value={editExpiresAt}
                    onChange={(e) => setEditExpiresAt(e.target.value)}
                    className="w-full px-4 py-2 bg-bg-1 border border-border-1 rounded-lg text-text-1 focus:outline-none focus:ring-2 focus:ring-accent-1"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 p-4 border-t border-border-1">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 bg-bg-3 text-text-2 hover:bg-bg-3/80 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleUpdateApiKey}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-accent-1 hover:bg-accent-1/90 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {saving && <RefreshCw size={16} className="animate-spin" />}
                  Salvar
                </button>
              </div>
            </div>
          </div>
        )}

        <ConfirmDialog {...dialogProps} />
      </main>
    </div>
  );
}
